import { ScrollView, View } from 'react-native'
import Typography from '@ca/ui/typography'
import Button from '@ca/ui/buttons/Button'
import { PanelBox } from './PanelBox'
import { Row } from './Row'
import { hsaPromptForDebug } from './constants'
import { Phase } from './types'
import { fmt } from './utils'
import { useFreezeStatus, useHsaFaceData } from './faceDataStore'
import type { HsaResult } from './useHSACapture'
import type { HsaData, HsaDebug } from '../index'

type Props = {
  phase: Phase
  result: HsaResult | null
  debug: HsaDebug | null
  capturedCount: number
  targetCount: number
  progress: number
  onStart: () => void
  onStop: () => void
  onClear: () => void
  onRerunFrozenFrame: () => void
  onOpenSettings: () => void
}

const phaseLabels: Record<Phase, string> = {
  idle: 'Idle',
  calibrating: 'Setting Up',
  detecting: 'Detecting',
  findingFrame: 'Finding Lens Geometry',
  tooClose: 'Too Close',
  tooFar: 'Too Far',
  positioning: 'Positioning',
  opening: 'Opening',
  stabilising: 'Stabilising',
  sampling: 'Sampling',
  complete: 'Complete',
  failed: 'Failed',
}

function ResultRows({ result }: { result: HsaData | null }) {
  if (!result) {
    return <Row label="HSA Result" value="-" />
  }

  return <Row label="HSA Result" value={`${fmt(result.value, 2)} mm`} />
}

function DebugRows({ debug }: { debug: HsaDebug | null }) {
  const freezeStatus = useFreezeStatus()
  return (
    <View className="gap-1">
      <Typography variant="XS" className="mb-1 text-black/30">
        Debug
      </Typography>
      <Row label="Confidence" value={debug ? fmt(debug.confidence, 2) : '-'} />
      <Row label="Rejection" value={debug?.rejectionReason ?? '-'} />
      <Row label="Detector" value={debug?.detectorRan ? 'RAN' : 'WAITING'} />
      <Row label="ROI Side" value={debug?.roiSide ?? '-'} />
      <Row
        label="Candidates"
        value={debug?.candidateCount != null ? `${debug.candidateCount}` : '-'}
      />
      {debug?.selectedCandidate && (
        <Row
          label="Selected"
          value={`${fmt(debug.selectedCandidate.confidence, 2)} / ${debug.selectedCandidate.pointCount} pts`}
        />
      )}
      <Row
        label="Freeze"
        value={
          freezeStatus.active
            ? `${freezeStatus.source}${freezeStatus.reason ? ` / ${freezeStatus.reason}` : ''}`
            : 'live'
        }
      />
      <Row label="Side Accepted" value={debug?.sidePoseAccepted ? 'YES' : 'NO'} />
      {debug?.rotationY != null && (
        <Row
          label="Pose"
          value={`Y ${fmt(debug.rotationY)}° / X ${fmt(
            debug.rotationX,
            0
          )}°`}
        />
      )}
      {debug?.cameraRotationY != null && (
        <Row
          label="Camera Y"
          value={`${fmt(debug.cameraRotationY)}°`}
        />
      )}
      {debug?.rotationSource && (
        <Row label="Pose Source" value={debug.rotationSource} />
      )}
      {debug?.minSideRotationY != null && debug.maxSideRotationY != null && (
        <Row
          label="Side Gate"
          value={`${fmt(debug.minSideRotationY, 0)}-${fmt(
            debug.maxSideRotationY,
            0
          )}°`}
        />
      )}
    </View>
  )
}

export function HSATab({
  phase,
  result,
  debug,
  capturedCount,
  targetCount,
  progress,
  onStart,
  onStop,
  onClear,
  onRerunFrozenFrame,
  onOpenSettings,
}: Props) {
  const data = useHsaFaceData()
  const isIdle = phase === 'idle'
  const isComplete = phase === 'complete'
  const isMeasuring = !isIdle && !isComplete
  const liveResult = result ?? data?.hsa ?? null
  const liveDebug = debug ?? data?.hsaDebug ?? null
  const hasCaptureState = phase !== 'idle' || result != null

  return (
    <ScrollView
      className="flex-1"
      contentContainerClassName="flex-grow gap-3 px-4 py-3 pb-12"
    >
      <PanelBox className="flex-1 gap-3">
        <View className="flex-row justify-end">
          <Button
            size="sm"
            variant="outlined"
            iconStart="settings"
            onPress={onOpenSettings}
          />
        </View>

        <View className="gap-1">
          <Typography variant="XS" className="mb-1 text-black/30">
            Phase
          </Typography>
          <Row label="State" value={phaseLabels[phase]} />
          <Row label="Prompt" value={hsaPromptForDebug(phase, liveDebug)} />
          {hasCaptureState && (
            <>
              <Row
                label="Captured Frames"
                value={`${capturedCount} / ${targetCount}`}
              />
              <View className="h-2 overflow-hidden rounded-full bg-black/10">
                <View
                  className="h-full rounded-full bg-green-500"
                  style={{ width: `${Math.round(progress * 100)}%` }}
                />
              </View>
            </>
          )}
        </View>

        <View className="gap-1">
          <Typography variant="XS" className="mb-1 text-black/30">
            Result
          </Typography>
          <ResultRows result={liveResult} />
        </View>

        <DebugRows debug={liveDebug} />

        <View className="mt-auto gap-2">
          <Button
            width="full"
            variant="filled"
            label="Start HSA"
            disabled={!isIdle}
            onPress={onStart}
          />
          <Button
            width="full"
            variant="outlined"
            label="Rerun Frozen HSA"
            onPress={onRerunFrozenFrame}
          />
          <Button
            width="full"
            variant="outlined"
            label="Cancel"
            disabled={!isMeasuring}
            onPress={onStop}
          />
          <Button
            width="full"
            variant="outlined"
            label="Clear"
            disabled={isIdle && !result}
            onPress={onClear}
          />
        </View>
      </PanelBox>
    </ScrollView>
  )
}
